import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SecretOutsideDiffDto, SecretStatus } from "@reviewer73/shared";
import { ExternalLink } from "lucide-react";
import { type CSSProperties, Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../api/client.js";
import { Rack } from "../components/rack.js";
import { StaggerBody, StaggerRow } from "../components/stagger.js";
import { Button } from "../components/ui/button.js";
import { Input } from "../components/ui/input.js";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select.js";
import { Table, TableCell, TableEmpty, TableHead, TableHeader, TableRow, TableSkeleton } from "../components/ui/table.js";
import { formatDate, repoName } from "../lib/format.js";

/* The same dot-plus-label vocabulary review states use — an open fund is the
 * only one that reads as a to-do, so it takes the failed red; the two verdicts
 * take done-green and the muted gray, and a fund the scanner simply stopped
 * seeing stays neutral. */
const STATUS_CONFIG: Record<SecretStatus, { label: string; dot: string }> = {
  open: { label: "offen", dot: "var(--hold-failed)" },
  resolved: { label: "erledigt", dot: "var(--hold-done)" },
  ignored: { label: "ignoriert", dot: "var(--hold-skipped)" },
  gone: { label: "verschwunden", dot: "var(--handle-gray)" },
};

const FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "open", label: "offen" },
  { value: "resolved", label: "erledigt" },
  { value: "ignored", label: "ignoriert" },
  { value: "gone", label: "verschwunden" },
  { value: "all", label: "alle" },
];

const EMPTY_COPY: Record<string, string> = {
  open: "Keine offenen Funde — gitleaks sieht außerhalb der Diffs derzeit kein Secret im Repo.",
  resolved: "Noch nichts als erledigt abgelegt — hier landen Funde, die tatsächlich behoben wurden.",
  ignored: "Noch nichts ignoriert — hier landen Funde, die bewusst als harmlos abgehakt wurden.",
  gone: "Kein Fund ist von selbst verschwunden — hier landet, was ein späterer Scan nicht mehr gesehen hat.",
  all: "Für dieses Projekt hat gitleaks noch nie ein Secret außerhalb eines Diffs gemeldet.",
};

function StatusDot({ status }: { status: SecretStatus }) {
  const { label, dot } = STATUS_CONFIG[status];
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-medium">
      <span aria-hidden className="dot" style={{ "--dot": dot } as CSSProperties} />
      {label}
    </span>
  );
}

/** GitLab resolves `HEAD` to the default branch, so no branch name has to be stored. */
function blobUrl(gitlabUrl: string, projectPath: string, file: string, line: number) {
  return `${gitlabUrl.replace(/\/$/, "")}/${projectPath}/-/blob/HEAD/${file}#L${line}`;
}

/**
 * The verdict, entered in the row instead of in a modal — nothing here needs
 * protected focus, and the fund stays readable above the form. The comment is
 * the whole point of the gesture, so it is required.
 */
function VerdictForm({
  onSubmit,
  onCancel,
  pending,
}: {
  onSubmit: (kind: "ignored" | "resolved", comment: string) => void;
  onCancel: () => void;
  pending: boolean;
}) {
  const [kind, setKind] = useState<"ignored" | "resolved">("resolved");
  const [comment, setComment] = useState("");

  return (
    <form
      className="flex flex-wrap items-center gap-2 rounded-xl bg-muted/60 p-3"
      onKeyDown={(event) => {
        if (event.key === "Escape") onCancel();
      }}
      onSubmit={(event) => {
        event.preventDefault();
        if (!comment.trim()) return;
        onSubmit(kind, comment.trim());
      }}
    >
      <Select value={kind} onValueChange={(v) => setKind(v as "ignored" | "resolved")}>
        <SelectTrigger className="w-40" aria-label="Art des Urteils">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="resolved">erledigt</SelectItem>
          <SelectItem value="ignored">ignoriert</SelectItem>
        </SelectContent>
      </Select>
      <Input
        autoFocus
        required
        value={comment}
        onChange={(event) => setComment(event.target.value)}
        aria-label="Begründung"
        placeholder={
          kind === "resolved"
            ? "Was wurde getan? Etwa: Token rotiert und aus dem Repo entfernt."
            : "Warum ist der Fund harmlos? Etwa: Beispielwert in der Dokumentation."
        }
        className="min-w-56 flex-1"
      />
      <Button type="submit" size="sm" disabled={pending || !comment.trim()}>
        {pending ? "Wird gespeichert…" : "Ablegen"}
      </Button>
      <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
        Abbrechen
      </Button>
    </form>
  );
}

export function Security() {
  const [status, setStatus] = useState("open");
  const [projectId, setProjectId] = useState("all");
  const [judging, setJudging] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const me = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false });
  const projects = useQuery({ queryKey: ["projects"], queryFn: api.projects });
  const secrets = useQuery({
    queryKey: ["secrets-outside-diff", status, projectId],
    queryFn: () =>
      api.secretsOutsideDiff({ status, projectId: projectId === "all" ? undefined : projectId }),
  });

  // Both mutations move a row out of the current filter, and the nav badge reads
  // the same key family — so one invalidation keeps screen and badge in step.
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["secrets-outside-diff"] });

  const judge = useMutation({
    mutationFn: ({ id, kind, comment }: { id: string; kind: "ignored" | "resolved"; comment: string }) =>
      api.dismissSecret(id, { kind, comment }),
    onSuccess: async (_data, variables) => {
      setJudging(null);
      await invalidate();
      toast.success(variables.kind === "resolved" ? "Als erledigt abgelegt" : "Als harmlos ignoriert");
    },
    onError: (error: Error) => toast.error(`Konnte nicht abgelegt werden: ${error.message}`),
  });

  const reopen = useMutation({
    mutationFn: (id: string) => api.restoreSecret(id),
    onSuccess: async () => {
      await invalidate();
      toast.success("Fund wieder offen");
    },
    onError: (error: Error) => toast.error(`Konnte nicht geöffnet werden: ${error.message}`),
  });

  const rows: SecretOutsideDiffDto[] = secrets.data ?? [];

  if (secrets.error) {
    return (
      <div className="tile p-6 text-sm text-muted-foreground">
        Funde konnten nicht geladen werden. Seite neu laden — wenn das nicht hilft, läuft das
        Backend nicht.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-baseline gap-2 text-xl font-bold tracking-tight">
          Sicherheit
          {!secrets.isLoading ? (
            <span className="text-sm font-normal tabular-nums text-muted-foreground">{rows.length}</span>
          ) : null}
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="w-56" aria-label="Projekt filtern">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">alle Projekte</SelectItem>
              {(projects.data ?? []).map((project) => (
                <SelectItem key={project.id} value={project.id}>
                  {repoName(project.pathWithNamespace)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" aria-label="Status filtern">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FILTER_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Rack
        label="Secrets außerhalb des Diffs"
        hint="Von gitleaks im gesamten Repo gefunden, nicht nur auf geänderten Zeilen — landet nie als MR-Kommentar. Verschwindet von selbst, sobald ein Scan den Fund nicht mehr sieht."
      >
        <div className="tile p-1">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead>Projekt</TableHead>
                <TableHead>Fundstelle</TableHead>
                <TableHead>Beschreibung</TableHead>
                <TableHead>Zuerst gesehen</TableHead>
                <TableHead className="text-right">Urteil</TableHead>
              </TableRow>
            </TableHeader>
            {secrets.isLoading ? (
              <TableSkeleton colSpan={6} />
            ) : rows.length === 0 ? (
              <TableEmpty colSpan={6}>{EMPTY_COPY[status] ?? EMPTY_COPY.all}</TableEmpty>
            ) : (
              <StaggerBody>
                {rows.map((secret) => (
                  <Fragment key={secret.id}>
                  <StaggerRow>
                    <TableCell>
                      <StatusDot status={secret.status} />
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/projects/${secret.projectId}`}
                        className="underline decoration-transparent underline-offset-4 transition-colors hover:decoration-current"
                      >
                        {repoName(secret.projectPath)}
                      </Link>
                    </TableCell>
                    <TableCell>
                      {me.data?.gitlabUrl ? (
                        <a
                          href={blobUrl(me.data.gitlabUrl, secret.projectPath, secret.file, secret.line)}
                          target="_blank"
                          rel="noreferrer"
                          title="Datei in GitLab öffnen"
                          className="inline-flex items-center gap-1.5 font-mono underline decoration-transparent underline-offset-4 transition-colors hover:decoration-current"
                        >
                          <span className="break-all">
                            {secret.file}:{secret.line}
                          </span>
                          <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
                        </a>
                      ) : (
                        <span className="font-mono break-all">
                          {secret.file}:{secret.line}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {secret.description}
                      {secret.dismissedAt ? (
                        <span className="mt-1 block text-muted-foreground">
                          „{secret.dismissedComment}“ — {secret.dismissedBy ?? "unbekannt"},{" "}
                          {formatDate(secret.dismissedAt)}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {formatDate(secret.firstSeenAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      {secret.dismissedAt ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={reopen.isPending}
                          onClick={() => reopen.mutate(secret.id)}
                        >
                          Wieder öffnen
                        </Button>
                      ) : judging === secret.id ? (
                        <span className="text-xs text-muted-foreground">wird beurteilt</span>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => setJudging(secret.id)}>
                          Beurteilen
                        </Button>
                      )}
                    </TableCell>
                  </StaggerRow>
                  {/* The form takes the full row width instead of the action
                      cell — a comment field squeezed into the last column wraps
                      to nothing on a laptop. */}
                  {judging === secret.id ? (
                    <tr className="border-b border-border last:border-0">
                      <td colSpan={6} className="p-2">
                        <VerdictForm
                          pending={judge.isPending}
                          onCancel={() => setJudging(null)}
                          onSubmit={(kind, comment) => judge.mutate({ id: secret.id, kind, comment })}
                        />
                      </td>
                    </tr>
                  ) : null}
                  </Fragment>
                ))}
              </StaggerBody>
            )}
          </Table>
        </div>
      </Rack>
    </div>
  );
}
